/*
 * Copyright (C) 2025 JPMorgan Chase & Company
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, is not permitted.
 */

import React, {useContext, useEffect, useState} from 'react';
import {ExdsGrid, ExdsLayout, ExdsText} from '@exds/core';
import './CreatePin.scss';
import PasswordInputGroup from '../../../PasswordInputGroup/PasswordInputGroup';
import ButtonGroup from '../../../ButtonGroup/ButtonGroup';
import RsaHeader from '../RsaHeader/RsaHeader';
import {apiPost} from "../../../../service/service";
import AppContext from "../../../../AppContext";
import {doDecrypt, doEncrypt, generateKeyPair} from "../../../E2ee/E2ee";
import {TuxLoadingIndicator} from "@tuxedo/react-component-library";
import Alert from "../../../Alert/Alert";
 import {useNavigate} from "react-router-dom";
import {addPageLoadEvent} from "../../../AdobeAnalytics/AdobeAnalytics";
import {STEP_PERFORMED} from "../../../../constants/constants";

export default function CreatePin(props) {
  const { setActiveMobileAppSetupPage, setPageName } = props;
  const [formSubmitted, setFormSubmitted] = useState(false);
  const {
    sidContext,
    mobileDeviceContext,
    qrCodeContext,
    phoneNumberContext,
    transactionIdContext,
    expirationTimeContext,
    hashContext,
    hashStepsStatusContext,
    loadAA
  } = useContext(AppContext);
  const phoneNumber = phoneNumberContext.at(0);
  const mobileDevice = mobileDeviceContext.at(0);
  const sid = sidContext.at(0);
  const setQrCodeUrl = qrCodeContext.at(1);
  const transactionId = transactionIdContext.at(0);
  const expirationTime = expirationTimeContext.at(0);
  const [hash, setHash] = hashContext;
  const [hashStepsStatus, setHashStepsStatus] = hashStepsStatusContext;
  const [keyPair, setKeyPair] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState({
    hasCredentialPolicyError: false,
    hasSubmitError: false,
    correlationId: null
  });
  const navigate = useNavigate();
  const [isAdvanceButtonDisabled, setIsAdvanceButtonDisabled] = useState(true);
  const pageName = 'Mobile App Setup | RSA Create Pin';

  useEffect(() => {
    if(loadAA) {
      setPageName(pageName);
      addPageLoadEvent(pageName, sid);
    }
  }, [loadAA, sid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const setKeys = async () => {
      const pair = await generateKeyPair();
      setKeyPair(pair);
    };

    setKeys();
  }, []);


  const modalDetails = {
    title: 'Are you sure you want to cancel Mobile App Setup?',
    modalActionButton: 'Yes, I want to cancel',
    continueButton: 'No, let\'s continue'
  };

  const handleBackAction = () => {
    setActiveMobileAppSetupPage('downloadRsa');
  };

  const submitForm = async (e) => {
    e.preventDefault();
    setFormSubmitted(true);
    error.hasSubmitError = false;

    const target = await e.target;
    const formData = target.elements;

    const errorMessages = document.querySelectorAll('[aria-describedby*="error-text"]');
    const validationErrorMessage = document.querySelectorAll('[aria-describedby*="label-validation"]');

    const pinValue = formData['password-input-field-input'].value;
    const confirmPinValue = formData['confirm-password-input-field-input'].value;

    const formValid = errorMessages.length === 0 && validationErrorMessage.length === 0 && pinValue.length > 0
      && confirmPinValue.length > 0 && pinValue === confirmPinValue;

    if (formValid) {
      setIsLoading(true);
      apiPost('/api/requestSoftToken', {
        sid,
        deviceType: mobileDevice,
        initialPin: await doEncrypt(pinValue),
        encryptedPhoneNumber: phoneNumber,
        encryptedTransactionId: transactionId,
        expirationTime,
        rsaSetup: hashStepsStatus.rsaSetup,
        sloSetup: hashStepsStatus.sloSetup,
        sloTest: hashStepsStatus.sloTest,
        hash
      }, {'PUBLIC_KEY': keyPair.publicKey})
        .then(async (response) => {
          if (response.status === 200) {
            const encryptedLauncherUrl = response.data.result.newSoftToken.launcherUrl;
            if(encryptedLauncherUrl && encryptedLauncherUrl !== '') {
              const decryptedLauncherUrl = await doDecrypt(encryptedLauncherUrl, keyPair);
              setQrCodeUrl(decryptedLauncherUrl);
              setHash(response.data.result.hash);
              setHashStepsStatus({
                ...hashStepsStatus,
                rsaSetup: STEP_PERFORMED,
              });
              setActiveMobileAppSetupPage('activateToken');
            }
            else {
              setFormSubmitted(false);
              setIsLoading(false);
              error.correlationId = response.correlationId;
              error.hasSubmitError = true;
            }
          } else {
            setFormSubmitted(false);
            setIsLoading(false);
            error.correlationId = response.correlationId;
            error.hasSubmitError = true;
          }
        });
    }
  };
  return (
    <>
      {isLoading && <div className="loading-spinner"><TuxLoadingIndicator/></div>}
      {!isLoading && <ExdsGrid
        columnSpan={8}
        className='step-details'
      >
        <ExdsLayout
          alignItems="flex-start"
          display="flex"
          flexDirection="column"
        >
          {
            (error.hasCredentialPolicyError && <Alert correlationId={error.correlationId} loadAA={loadAA} pageName={pageName} sid={sid}/>) ||
            (error.hasSubmitError && <Alert correlationId={error.correlationId} loadAA={loadAA} pageName={pageName} sid={sid}/>)
          }
          <RsaHeader />
          <form onSubmit={submitForm} noValidate>
            <ExdsText
              primitive="p"
              textStyle="bodySmallHeavier"
              className="step-number"
            >
              Step 2 of 3
            </ExdsText>
            <ExdsText
              primitive="p"
              textStyle="bodyLargeHeavier"
              className="step-title"
            >
              Create your unique PIN
            </ExdsText>
            <ExdsText
              primitive="p"
              textStyle="bodyMedium"
              className="step-description"
            >
              You will use this PIN to generate a code every time you’re required to use RSA Authenticator.
            </ExdsText>
            <fieldset className="pin-group-container">
              <PasswordInputGroup formSubmitted={formSubmitted}
                                  type="token"
                                  showRequirements
                                  showCharCount
                                  passwordTypeLabel="PIN"
                                  firstInputLabel='Create'
                                  setError={setError}
                                  setIsButtonDisabled={setIsAdvanceButtonDisabled}
              />
            </fieldset>
            <ButtonGroup
              advanceButtonContent="Continue"
              isAdvanceButtonDisabled={isAdvanceButtonDisabled}
              modalButtonContent="Cancel"
              modalButtonType="button"
              modalDetails={modalDetails}
              modalAction={() => navigate('/finish')}
              backButtonContent="Back"
              backButtonAction={handleBackAction}
            />
          </form>
        </ExdsLayout>
      </ExdsGrid>}
    </>
  );
}
